import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Text being typed, kept in localStorage until it's sent, so a reload (a
 * deploy, a dropped connection, a closed tab) doesn't throw it away. `null`
 * key = plain state, for editors that have nothing to key the draft by.
 */
const PREFIX = "hub:draft:";
const MAX_AGE_MS = 14 * 24 * 60 * 60_000;

type Stored = { v: string; at: number };

function read(key: string): string | null {
  try {
    const raw = globalThis.localStorage?.getItem(PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Stored;
    if (typeof parsed?.v !== "string" || Date.now() - parsed.at > MAX_AGE_MS) return null;
    return parsed.v;
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    if (value.trim() === "") globalThis.localStorage?.removeItem(PREFIX + key);
    else
      globalThis.localStorage?.setItem(PREFIX + key, JSON.stringify({ v: value, at: Date.now() }));
  } catch {
    // Full or blocked storage: the draft just isn't kept.
  }
}

/** Drop drafts nobody came back to. Exported for the app shell to run once. */
export function pruneDrafts(): void {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return;
    for (let i = ls.length - 1; i >= 0; i--) {
      const k = ls.key(i);
      if (k?.startsWith(PREFIX) && read(k.slice(PREFIX.length)) === null) ls.removeItem(k);
    }
  } catch {
    /* nothing to prune */
  }
}

/**
 * `[value, setValue, clear]`. `initial` is what the editor shows when no
 * draft is stored (e.g. the current description). Call `clear` once the text
 * was saved.
 */
export function useDraft(
  key: string | null,
  initial = "",
): [string, (value: string) => void, () => void] {
  const [value, setValueState] = useState(() => (key ? (read(key) ?? initial) : initial));
  const keyRef = useRef(key);

  useEffect(() => {
    if (keyRef.current === key) return;
    keyRef.current = key;
    setValueState(key ? (read(key) ?? initial) : initial);
  }, [key, initial]);

  const setValue = useCallback(
    (next: string) => {
      setValueState(next);
      if (!key) return;
      if (next === initial) write(key, "");
      else write(key, next);
    },
    [key, initial],
  );

  const clear = useCallback(() => {
    if (key) write(key, "");
  }, [key]);

  return [value, setValue, clear];
}

/** Whether a draft is stored under `key` (to resume an edit after a reload). */
export function hasDraft(key: string): boolean {
  return read(key) !== null;
}
